import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Search as SearchIcon, Radio } from "lucide-react";
import api from "@/lib/api";
import Avatar from "@/components/Avatar";
import BottomNav from "@/components/BottomNav";

export default function Search() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    setSearching(true);
    timer.current = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(q.trim())}`)
        .then(({ data }) => setResults(data.users))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="min-h-screen max-w-md mx-auto px-4 py-6 pb-28" data-testid="search-page">
      <button data-testid="search-back" onClick={() => navigate(-1)} className="text-zinc-400 mb-5 flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>
      <h1 className="text-2xl font-extrabold font-display mb-4">Search 🔍</h1>
      <div className="flex items-center gap-2 bg-[#131A2A] border border-white/10 rounded-2xl px-4 py-3 focus-within:border-violet-500 transition-colors mb-5">
        <SearchIcon size={16} className="text-zinc-500" />
        <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search creators by name…"
          className="bg-transparent outline-none flex-1 text-white text-sm placeholder:text-zinc-500" />
      </div>

      {searching && <div className="space-y-2">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-14 rounded-2xl" />)}</div>}
      {!searching && q.trim().length >= 2 && results?.length === 0 && (
        <p className="text-zinc-500 text-xs text-center py-10" data-testid="search-no-results">No creators found for "{q}"</p>
      )}
      {!searching && q.trim().length < 2 && (
        <p className="text-zinc-500 text-xs text-center py-10">Type at least 2 characters to search</p>
      )}

      <div className="space-y-2" data-testid="search-results">
        {!searching && results?.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
            data-testid={`search-result-${u.id}`}
            onClick={() => navigate(`/profile/${u.id}`)}
            className="flex items-center gap-3 glass rounded-2xl px-3.5 py-2.5 cursor-pointer active:scale-[0.99] transition-transform">
            <Avatar src={u.avatar} size={40} level={u.level} tier={u.tier} />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold truncate">{u.name}</p>
              <p className="text-[10px] text-zinc-500">{u.tier} · Lv.{u.level} · {u.followers_count?.toLocaleString()} followers</p>
            </div>
            {u.is_live && (
              <button data-testid={`search-watch-${u.id}`}
                onClick={(e) => { e.stopPropagation(); navigate(`/stream/${u.live_stream_id}`); }}
                className="bg-rose-600 text-white text-[9px] font-extrabold px-2.5 py-1.5 rounded-full flex items-center gap-1 pulse-live shrink-0">
                <Radio size={10} /> LIVE
              </button>
            )}
          </motion.div>
        ))}
      </div>
      <BottomNav />
    </div>
  );
}
